/**
 * Pathology annotation store — image-space shapes + undo / redo.
 *
 * Pathology annotations are stored in whole-slide-image pixel space
 * so pan / zoom / compare / export all use the same coordinate
 * system. Every shape has:
 *   - a tool kind
 *   - a colour (from the active class palette)
 *   - a label (optional free text, e.g. "Focal DCIS")
 *   - the shape-specific geometry (rect / ellipse / polygon / ruler …)
 *
 * A bounded undo / redo stack keeps the last N states. When the user
 * ends a stroke or commits a shape we push onto `past`; `redo` moves
 * a snapshot back from `future`. This matches the muscle memory
 * pathologists already have from QuPath and HALO AP.
 */
import { createSignal } from "solid-js";

export type PathologyTool =
  | "move"     // default — select / drag / pan
  | "rect"
  | "ellipse"
  | "polygon"
  | "freehand"
  | "point"    // single-click landmark
  | "ruler"
  | "text"
  | "snapshot";  // view capture for report illustration

export interface PathPoint { x: number; y: number }

interface BaseAnno {
  id:       string;
  kind:     PathologyTool;
  color:    string;    // sRGB hex
  label?:   string;
  /** AI-generated findings carry their provenance. */
  source?:  "human" | "ai";
  aiModel?: string;
}

export type PathAnno =
  | (BaseAnno & { kind: "rect";     a: PathPoint; b: PathPoint })
  | (BaseAnno & { kind: "ellipse";  a: PathPoint; b: PathPoint })
  | (BaseAnno & { kind: "polygon";  points: PathPoint[] })
  | (BaseAnno & { kind: "freehand"; points: PathPoint[] })
  | (BaseAnno & { kind: "point";    at: PathPoint })
  | (BaseAnno & { kind: "ruler";    a: PathPoint; b: PathPoint })
  | (BaseAnno & { kind: "text";     at: PathPoint; text: string })
  | (BaseAnno & {
      kind: "snapshot";
      /** Base64 JPEG of the captured viewport (canvas + annotations). */
      dataUrl: string;
      /** View state at capture — pan + zoom used for click-to-restore. */
      panX: number;
      panY: number;
      zoom: number;
      /** Whether this snapshot is embedded in the exported PDF. */
      include: boolean;
      captureAt: number;
    });

export interface AnnotationState {
  items: PathAnno[];
}

const MAX_HISTORY = 50;

export function createAnnotationStore() {
  const [state,     setState]     = createSignal<AnnotationState>({ items: [] });
  const [past,      setPast]      = createSignal<AnnotationState[]>([]);
  const [future,    setFuture]    = createSignal<AnnotationState[]>([]);
  const [activeId,  setActiveId]  = createSignal<string | null>(null);

  function commit(next: AnnotationState) {
    setPast((p) => [...p.slice(-MAX_HISTORY + 1), state()]);
    setFuture([]);
    setState(next);
  }

  function add(a: PathAnno) {
    commit({ items: [...state().items, a] });
    setActiveId(a.id);
  }

  function replace(id: string, patch: Partial<PathAnno>) {
    commit({
      items: state().items.map((x) => x.id === id ? { ...x, ...patch } as PathAnno : x),
    });
  }

  function remove(id: string) {
    commit({ items: state().items.filter((x) => x.id !== id) });
    if (activeId() === id) setActiveId(null);
  }

  function clear() {
    commit({ items: [] });
    setActiveId(null);
  }

  function undo() {
    const p = past();
    if (!p.length) return;
    setFuture((f) => [state(), ...f].slice(0, MAX_HISTORY));
    setState(p[p.length - 1]);
    setPast(p.slice(0, -1));
  }

  function redo() {
    const f = future();
    if (!f.length) return;
    setPast((p) => [...p.slice(-MAX_HISTORY + 1), state()]);
    setState(f[0]);
    setFuture(f.slice(1));
  }

  return {
    state, activeId, setActiveId,
    add, replace, remove, clear, undo, redo,
    canUndo: () => past().length  > 0,
    canRedo: () => future().length > 0,
  };
}

// ---------- Class palette (HALO AP style) ----------
export interface AnnotationClass {
  id:    string;
  label: string;
  color: string;
  /** Keyboard shortcut 1..9 to select this class. */
  shortcut?: string;
}

export const PATHOLOGY_CLASSES: AnnotationClass[] = [
  { id: "tumor",   label: "Tumor",        color: "#f85149", shortcut: "1" },
  { id: "stroma",  label: "Stroma",       color: "#8b949e", shortcut: "2" },
  { id: "necrosis",label: "Necrosis",     color: "#d4a017", shortcut: "3" },
  { id: "immune",  label: "Immune",       color: "#1f6feb", shortcut: "4" },
  { id: "normal",  label: "Normal",       color: "#27a567", shortcut: "5" },
  { id: "other",   label: "Other",        color: "#a371f7", shortcut: "6" },
];
